import React, { useCallback, memo } from 'react';
import { Book } from '../../services/bookService';
import { Clock, GraduationCap, MessageCircle } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';
import { useContact } from '../../hooks/useContact';
import { BASE_URL } from '../../apiConfig';

interface BookCardProps {
  book: Book;
  isActive?: boolean;
  isPartial?: boolean;
  onClick?: () => void;
  position?: 'left' | 'center' | 'right';
}

const BookCard: React.FC<BookCardProps> = ({ 
  book, 
  isActive = true, 
  isPartial = false, 
  onClick, 
  position = 'center' 
}) => {
  const { openWhatsApp, whatsappNumber, isLoading } = useContact();

  // Memoized image error handler
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = '/default-course.jpg';
  }, []);

  // Memoized book image source getter
  const getBookImageSrc = useCallback((book: Book): string => {
    if (!book.imagePath) {
      return '/default-course.jpg';
    }
    
    if (book.imagePath.startsWith('http')) {
      return book.imagePath;
    }
    
    let imagePath = book.imagePath;
    if (imagePath.startsWith('/')) {
      imagePath = imagePath.substring(1);
    }
    
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : BASE_URL + '/';
    return `${baseUrl}${imagePath}`;
  }, []);

  // Handle WhatsApp click
  const handleWhatsAppClick = useCallback(() => {
    if (whatsappNumber) {
      const message = `مرحباً، أريد معلومات عن كتاب: ${book.title}`;
      openWhatsApp(message);
    }
  }, [book.title, whatsappNumber, openWhatsApp]);

  // Grade translator function
  const getGradeInArabic = (grade: string): string => {
    const gradeTranslations: Record<string, string> = {
      'Secondary1': 'أولى ثانوي',
      'Secondary2': 'ثانية ثانوي',
      'Secondary3': 'ثالثة ثانوي',
      'default': grade
    };
    return gradeTranslations[grade] || grade;
  };

  // Grade color function (matching CourseCard)
  const getGradeColor = (grade: string): string => {
    const colors: Record<string, string> = {
      'Secondary1': 'bg-blue-100 text-blue-800',
      'Secondary2': 'bg-purple-100 text-purple-800',
      'Secondary3': 'bg-green-100 text-green-800',
      'default': 'bg-gray-100 text-gray-800'
    };
    return colors[grade] || colors['default'];
  };

  const cardClasses = `${cardTheme.shell} ${cardTheme.shellHover} transform transition-all duration-500 w-full md:w-96 mx-auto ${
    isActive 
      ? 'scale-100 z-20 opacity-100' 
      : isPartial 
      ? 'scale-90 z-10 opacity-70 hover:opacity-90 hover:scale-95' 
      : 'scale-75 z-0 opacity-50'
  } ${position === 'left' ? '-translate-x-4' : position === 'right' ? 'translate-x-4' : ''}`;

  return (
    <div 
      className={`${cardClasses} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Top Banner Section */}
      <div className={`relative h-48 md:h-64 ${cardTheme.headerBanner} overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-4 left-4 w-16 h-16 bg-white/20 rounded-full"></div>
          <div className="absolute top-8 right-8 w-12 h-12 bg-white/20 rounded-full"></div>
          <div className="absolute bottom-4 left-8 w-20 h-20 bg-white/20 rounded-full"></div>
        </div>
        
        {/* Book Image */}
        <div className="absolute inset-0">
          <img
            src={getBookImageSrc(book)}
            alt={book.title}
            className="w-full h-full object-contain bg-white"
            onError={handleImageError}
            loading="lazy"
          />
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        </div>
        
        {/* Grade Badge */}
        <div className="absolute top-2 right-2">
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${getGradeColor(book.grade)}`}>
            {getGradeInArabic(book.grade)}
          </span>
        </div>
      </div>
      
      {/* Bottom White Card Section */}
      <div className={`p-3 md:p-4 relative -mt-2 ${cardTheme.innerPanel} mx-2 md:mx-4`}>
        <h3 className={`text-sm md:text-base font-bold mb-1.5 md:mb-2 text-center line-clamp-2 min-h-[2.5rem] md:min-h-[3rem] ${cardTheme.title}`}>
          {book.title}
        </h3>
        
        {/* Book Description */}
        {book.description && (
          <div className="text-center mb-2 md:mb-3">
            <p className="text-xs md:text-sm text-gray-600 line-clamp-2">
              {book.description}
            </p>
            <div className={`w-12 md:w-16 h-0.5 ${cardTheme.divider} mx-auto mt-1.5 md:mt-2`}></div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="mb-2 md:mb-3">
          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppClick}
            disabled={!whatsappNumber || isLoading}
            className={`w-full ${cardTheme.btnPrimary} py-1.5 md:py-2 px-2 md:px-3 rounded-lg shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1 md:gap-1.5 group ${
              (!whatsappNumber || isLoading) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <MessageCircle className="w-3 h-3 md:w-4 md:h-4 group-hover:scale-110 transition-transform duration-300" />
            )}
            <span className="text-xs md:text-sm">واتساب</span>
          </button>
        </div>
        
        {/* Book Details */}
        <div className="mb-2 md:mb-3">
          <div className="flex items-center justify-center text-xs md:text-sm text-gray-500">
            <GraduationCap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-green-700" />
            <span>{getGradeInArabic(book.grade)}</span>
          </div>
        </div>
        
        {/* Price Display */}
        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 md:gap-2 bg-white rounded-lg px-3 md:px-4 py-1.5 md:py-2 shadow-sm border border-gray-100">
            <span className="text-xs md:text-sm text-gray-500 font-medium">السعر:</span>
            <span className="text-base md:text-lg font-bold text-green-800">
              {book.price === 0 ? 'مجاناً' : book.price?.toLocaleString('ar-EG') || 'غير محدد'}
            </span>
            {book.price && book.price !== 0 && (
              <span className="text-gold-600 font-medium text-xs md:text-sm">جنيه</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(BookCard); 